import React, { useEffect, useState } from "react";
import { Row, Col, Spin } from "antd";
import {
  CarOutlined,
  UserOutlined,
  AimOutlined,
  LaptopOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ToolOutlined,
  AppstoreOutlined,
  UserSwitchOutlined,
  AppstoreAddOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  CoffeeOutlined,
  DesktopOutlined,
  WifiOutlined,
  SolutionOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { AiOutlineForm } from "react-icons/ai";
import dataService from "../../networks/api/service/dataService";

/* ────────────────────────────────────────────────────────────────────────
   DESIGN TOKENS — warm workshop palette: white "paper" surfaces, a single
   confident orange for action/identity, ink-brown for text (never pure
   black, keeps the warmth), plus a small curated set of status colors.
   ──────────────────────────────────────────────────────────────────────── */
const T = {
  bg: "#FFF8F2",
  surface: "#FFFFFF",
  orange: "#FF6A1F",
  orangeDeep: "#D9480F",
  orangeSoft: "#FFE7D2",
  orangeFaint: "#FFF1E6",
  ink: "#2B1D12",
  inkMuted: "#93816F",
  line: "#F4DCC4",
  success: "#1D9A6C",
  danger: "#E5484D",
  amber: "#F2A63A",
  indigo: "#5B5FE9",
  teal: "#0BA5A0",
};

/* Fonts + keyframes, injected once. Sora carries the numbers/headings,
   Inter carries the body/labels — a deliberate two-voice system. */
const GlobalStyle = () => (
 <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');

    .opd-root { font-family: 'Montserrat', sans-serif; }
    .opd-num { font-family: 'Montserrat', sans-serif; font-variant-numeric: tabular-nums; font-weight: 600; }
    .opd-head { font-family: 'Montserrat', sans-serif; font-weight: 700; }

    @keyframes opd-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.55); }
      70%  { box-shadow: 0 0 0 7px rgba(255,255,255,0); }
      100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
    }
    @keyframes opd-sheen {
      0%   { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    .opd-live-dot {
      animation: opd-pulse 1.8s infinite;
    }
    .opd-strip {
      background: linear-gradient(90deg, ${T.orange}, ${T.amber}, ${T.orange});
      background-size: 200% 100%;
      animation: opd-sheen 6s linear infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .opd-live-dot, .opd-strip { animation: none; }
    }

    .opd-card { transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease; }
    .opd-card:hover { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(217,72,15,0.14); border-color: ${T.orange}55; }
    .opd-ticket:hover { transform: translateY(-3px); box-shadow: 0 12px 26px rgba(217,72,15,0.16); }
  `}</style>
);

/* ─── Section shell: eyebrow + title + animated accent strip ───────────── */
const Section = ({ icon, eyebrow, title, children }) => (
  <div
    className="opd-root"
    style={{
      background: T.surface,
      borderRadius: 18,
      padding: "26px 28px 28px",
      marginBottom: 22,
      border: `1px solid ${T.line}`,
      boxShadow: "0 3px 14px rgba(43,29,18,0.05)",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
      <div
        style={{
          background: T.orangeSoft,
          borderRadius: 10,
          width: 38,
          height: 38,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          color: T.orangeDeep,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        {eyebrow && (
          <div
            className="opd-head"
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.09em",
              color: T.inkMuted,
              textTransform: "uppercase",
            }}
          >
            {eyebrow}
          </div>
        )}
        <div className="opd-head" style={{ fontSize: 18, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
          {title}
        </div>
      </div>
    </div>
    <div className="opd-strip" style={{ height: 3, borderRadius: 2, marginBottom: 20, opacity: 0.85 }} />
    {children}
  </div>
);

/* ─── Ticket-stub stat card, used for grievances (a "ticket" per case) ──── */
const TicketCard = ({ icon, title, value, accent, onClick }) => (
  <div
    className="opd-ticket"
    onClick={onClick}
    style={{
      position: "relative",
      background: T.surface,
      border: `1.5px dashed ${T.line}`,
      borderRadius: 14,
      padding: "18px 16px 16px",
      cursor: onClick ? "pointer" : "default",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      overflow: "hidden",
    }}
  >
    {/* ticket notches */}
    <div style={{ position: "absolute", top: "50%", left: -9, width: 18, height: 18, borderRadius: "50%", background: T.bg, transform: "translateY(-50%)" }} />
    <div style={{ position: "absolute", top: "50%", right: -9, width: 18, height: 18, borderRadius: "50%", background: T.bg, transform: "translateY(-50%)" }} />

    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: accent + "1A",
        color: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 17,
        marginBottom: 14,
      }}
    >
      {icon}
    </div>
    <div className="opd-num" style={{ fontSize: 28, fontWeight: 800, color: T.ink, lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ fontSize: 12.5, color: T.inkMuted, marginTop: 6, fontWeight: 500 }}>{title}</div>
  </div>
);

/* ─── Standard mini stat card (travel / assets) ─────────────────────────── */
const MiniCard = ({ icon, label, value, color = T.orange, onClick }) => (
  <div
    className="opd-card"
    onClick={onClick}
    style={{
      borderRadius: 13,
      cursor: onClick ? "pointer" : "default",
      background: T.surface,
      border: `1px solid ${T.line}`,
      borderLeft: `3px solid ${color}`,
      padding: "14px 18px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          background: color + "16",
          borderRadius: 10,
          width: 42,
          height: 42,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div className="opd-num" style={{ fontSize: 21, fontWeight: 700, color: T.ink, lineHeight: 1 }}>
          {value}
        </div>
        <div style={{ fontSize: 12, color: T.inkMuted, marginTop: 4, fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  </div>
);

/* ─── Command bar pill (used in the top hero strip) ─────────────────────── */
const HeroPill = ({ label, value }) => (
  <div
    style={{
      background: "rgba(255,255,255,0.14)",
      border: "1px solid rgba(255,255,255,0.22)",
      borderRadius: 12,
      padding: "10px 16px",
      minWidth: 108,
    }}
  >
    <div className="opd-num" style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.82)", marginTop: 3, fontWeight: 500 }}>{label}</div>
  </div>
);

/* ─── Employee type → icon + color map (curated, orange-led palette) ───── */
const EMPLOYEE_TYPE_CONFIG = {
  totalHouseKeeping:        { label: "House Keeping",     icon: <CoffeeOutlined />,        color: T.orange },
  totalSecurity:            { label: "Security",           icon: <SafetyOutlined />,         color: T.danger },
  totalKitchenStaff:        { label: "Kitchen Staff",      icon: <CoffeeOutlined />,         color: T.amber },
  totalComputerStaff:       { label: "Computer Staff",     icon: <DesktopOutlined />,        color: T.indigo },
  totalITStaff:             { label: "IT Staff",            icon: <WifiOutlined />,           color: T.teal },
  totalSocialMediaStaff:    { label: "Social Media Staff",  icon: <AppstoreOutlined />,       color: T.indigo },
  totalAdministrativeStaff: { label: "Administrative",      icon: <SolutionOutlined />,       color: T.orangeDeep },
  totalMaintenanceStaff:    { label: "Maintenance Staff",   icon: <ToolOutlined />,           color: T.success },
  totalOthers:              { label: "Others",              icon: <QuestionCircleOutlined />, color: T.inkMuted },
};

/* ─── Asset status config ───────────────────────────────────────────────── */
const ASSET_CONFIG = [
  { key: "totalWorkingAsset",     label: "Working",     icon: <CheckCircleOutlined />, color: T.success },
  { key: "totalNotWorkingAsset",  label: "Not Working", icon: <CloseCircleOutlined />, color: T.danger },
  { key: "totalMaintenanceAsset", label: "Maintenance", icon: <ToolOutlined />,        color: T.amber },
];

/* ─── Grievance card accent colors (status meaning, not decoration) ─────── */
const GRIEVANCE_ACCENT = {
  Assigned: T.indigo,
  Raised: T.orange,
  Solved: T.success,
  Pending: T.amber,
  Rejected: T.danger,
};

// ══════════════════════════════════════════════════════════════════════════
const Dashboard = ({ data, grievanceList, assignedGrievance }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Grievance data from props ──────────────────────────────────────────
  const grievanceRaise =
    data?.find((item) => item.grievanceRaise)?.grievanceRaise || {};
  const assignedGrievanceData =
    data?.find((item) => item.assignedGrievance) || {};
  const solvedGrievanceData =
    data?.find((item) => item.solvedGrievance) || {};
  const pendingGrievanceData =
    data?.find((item) => item.pendingGrievance) || {};
  const rejectedGrievanceData =
    data?.find((item) => item.rejectedGrievance) || {};

  // ── Travel/Asset/Employee data from separate API ───────────────────────
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await dataService.dashboardForTrip();
        setDashboardData(res.data[0]);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const handleCardClick = (section) => {
    const routes = {
      vehicle: "/login/vehicle-management",
      driver:  "/login/driver-management",
      trip:    "/login/trip-management",
    };
    if (routes[section]) navigate(routes[section]);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "80px 0", background: T.bg, minHeight: "100vh" }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  const { vehicle, driver, trip, asset, employee } = dashboardData || {};

  // ── Grievance cards config ─────────────────────────────────────────────
  const grievanceCards = [
    {
      title: "Assigned",
      value: assignedGrievanceData?.assignedGrievance?.totalAssignedGrivance ?? 0,
      icon: <UserSwitchOutlined />,
      nav: "grievance-type/ASSIGNED",
    },
    {
      title: "Raised",
      value: grievanceRaise?.totalCreatedGrievance ?? 0,
      icon: <AppstoreAddOutlined />,
      nav: "grievance-type/ALL",
    },
    {
      title: "Solved",
      value: solvedGrievanceData?.solvedGrievance?.totalSolvedGrievance ?? 0,
      icon: <CheckCircleOutlined />,
      nav: "grievance-type/CLOSED",
    },
    {
      title: "Pending",
      value: pendingGrievanceData?.pendingGrievance?.totalPendingGrievance ?? 0,
      icon: <ClockCircleOutlined />,
      nav: "grievance-type/OPEN",
    },
    {
      title: "Rejected",
      value: rejectedGrievanceData?.rejectedGrievance?.totalRejectedGrievance ?? 0,
      icon: <CloseCircleOutlined />,
      nav: "grievance-type/REJECT",
    },
  ];

  // ── Dynamically build employee breakdown cards (skip zero values) ──────
  const employeeBreakdownCards = Object.entries(EMPLOYEE_TYPE_CONFIG)
    .map(([key, config]) => ({
      ...config,
      value: employee?.[key] ?? 0,
    }))
    .filter((item) => item.value > 0); // only show types that have employees

  const pendingCount = pendingGrievanceData?.pendingGrievance?.totalPendingGrievance ?? 0;
  const needsAttention = (asset?.totalNotWorkingAsset ?? 0) + (asset?.totalMaintenanceAsset ?? 0);

  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="opd-root" style={{ padding: "20px", background: T.bg, minHeight: "100vh" }}>
      <GlobalStyle />

      {/* ── Command bar (hero) ────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(120deg, ${T.orangeDeep} 0%, ${T.orange} 60%, ${T.amber} 130%)`,
          borderRadius: 20,
          padding: "22px 26px",
          marginBottom: 22,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          boxShadow: "0 10px 28px rgba(217,72,15,0.28)",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              className="opd-live-dot"
              style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", display: "inline-block" }}
            />
            <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em", color: "rgba(255,255,255,0.85)", textTransform: "uppercase" }}>
              Day - {today}
            </span>
          </div>
          <div className="opd-head" style={{ fontSize: 25, fontWeight: 800, color: "#fff", marginTop: 6 }}>
            Operations Dashboard
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <HeroPill label="Pending Grievances" value={pendingCount} />
          <HeroPill label="Vehicles Available" value={vehicle?.availableVehicle ?? 0} />
          <HeroPill label="Assets Needing Attention" value={needsAttention} />
          <HeroPill label="Total Workforce" value={employee?.totalEmployee ?? 0} />
        </div>
      </div>

      {/* ── Grievance Section ─────────────────────────────────────────── */}
      <Section icon={<AiOutlineForm />} eyebrow="Case Tickets" title="Grievances">
        <Row gutter={[16, 16]}>
          {grievanceCards.map((card, i) => (
            <Col key={i} xs={12} sm={8} md={8} lg={4} xl={4}>
              <TicketCard
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={GRIEVANCE_ACCENT[card.title] || T.orange}
                onClick={() => navigate(`/login/${card.nav}`)}
              />
            </Col>
          ))}
        </Row>
      </Section>

      {/* ── Travel Section ────────────────────────────────────────────── */}
      <Section icon={<CarOutlined />} eyebrow="Fleet" title="Travel Details">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8} md={8}>
            <MiniCard
              icon={<CarOutlined />}
              label="Vehicles"
              value={vehicle?.totalVehicle ?? 0}
              color={T.orange}
              onClick={() => handleCardClick("vehicle")}
            />
            <div style={{ marginTop: 10 }}>
              <MiniCard
                icon={<CheckCircleOutlined />}
                label="Available Vehicles"
                value={vehicle?.availableVehicle ?? 0}
                color={T.success}
              />
            </div>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <MiniCard
              icon={<UserOutlined />}
              label="Drivers"
              value={driver?.totalDriver ?? 0}
              color={T.indigo}
              onClick={() => handleCardClick("driver")}
            />
            <div style={{ marginTop: 10 }}>
              <MiniCard
                icon={<CheckCircleOutlined />}
                label="Available Drivers"
                value={driver?.availableDriver ?? 0}
                color={T.success}
              />
            </div>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <MiniCard
              icon={<AimOutlined />}
              label="Trips"
              value={trip?.totalTrip ?? 0}
              color={T.orangeDeep}
              onClick={() => handleCardClick("trip")}
            />
            <div style={{ marginTop: 10 }}>
              <MiniCard
                icon={<CheckCircleOutlined />}
                label="Finished Trips"
                value={trip?.finishedTrip ?? 0}
                color={T.success}
              />
            </div>
          </Col>
        </Row>
      </Section>

      {/* ── Asset Management Section ──────────────────────────────────── */}
      <Section icon={<LaptopOutlined />} eyebrow="Inventory" title="Asset Management">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <MiniCard
              icon={<LaptopOutlined />}
              label="Total Assets"
              value={asset?.totalAsset ?? 0}
              color={T.orange}
              onClick={() => navigate("/login/asset-management")}
            />
          </Col>
          {ASSET_CONFIG.map((cfg) => (
            <Col key={cfg.key} xs={24} sm={12} md={6}>
              <MiniCard
                icon={cfg.icon}
                label={cfg.label}
                value={asset?.[cfg.key] ?? 0}
                color={cfg.color}
              />
            </Col>
          ))}
        </Row>
      </Section>

      {/* ── Employee Management Section ───────────────────────────────── */}
      <Section icon={<TeamOutlined />} eyebrow="Workforce" title="Employee Management">
        {/* Hero total banner */}
        <div
          onClick={() => navigate("/login/employee-management")}
          style={{
            background: `linear-gradient(120deg, ${T.orangeDeep}, ${T.orange})`,
            borderRadius: 14,
            padding: "16px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
            cursor: "pointer",
            boxShadow: "0 6px 18px rgba(217,72,15,0.25)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                background: "rgba(255,255,255,0.18)",
                borderRadius: 11,
                width: 46,
                height: 46,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                color: "#fff",
              }}
            >
              <TeamOutlined />
            </div>
            <div>
              <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 11.5, fontWeight: 600, letterSpacing: "0.04em" }}>
                TOTAL WORKFORCE
              </div>
              <div className="opd-num" style={{ color: "#fff", fontSize: 30, fontWeight: 800, lineHeight: 1.15 }}>
                {employee?.totalEmployee ?? 0}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.18)", borderRadius: 8, padding: "6px 12px", color: "#fff", fontSize: 12.5, fontWeight: 600 }}>
            View all <ThunderboltOutlined />
          </div>
        </div>

        {/* Breakdown grid */}
        {employeeBreakdownCards.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
            {employeeBreakdownCards.map((emp, i) => (
              <div
                key={i}
                className="opd-card"
                style={{
                  background: T.orangeFaint,
                  border: `1px solid ${T.line}`,
                  borderLeft: `3px solid ${emp.color}`,
                  borderRadius: 12,
                  padding: "13px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                }}
              >
                <div
                  style={{
                    background: emp.color + "16",
                    borderRadius: 9,
                    width: 34,
                    height: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    color: emp.color,
                    flexShrink: 0,
                  }}
                >
                  {emp.icon}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="opd-num" style={{ fontSize: 19, fontWeight: 700, color: T.ink, lineHeight: 1 }}>
                    {emp.value}
                  </div>
                  <div style={{ fontSize: 10.5, color: T.inkMuted, marginTop: 4, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {emp.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "30px 0", color: T.inkMuted, fontSize: 13 }}>
            <TeamOutlined style={{ fontSize: 28, display: "block", marginBottom: 8, color: T.orangeSoft }} />
            No employee breakdown data available.
          </div>
        )}
      </Section>
    </div>
  );
};

export default Dashboard;